import { Button } from '@/components/ui/button';
import type { AIActionType } from '@/features/rich-text/hooks/use-improve-writing';
import { useVoiceRecording } from '@/lib/contexts/voice-recording-context';
import { useLocale } from '@/lib/hooks/use-locale';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';
import React, { useState } from 'react';
import type { RichTextInputProps } from './rich-text-input';
import { RichTextInput, type RichTextValue } from './rich-text-input';

type RichTextForm = {
    allowEmpty?: boolean;
    onSubmit: (content: RichTextValue, aiSessionId?: string) => void;
    onCancel: () => void;
    isSubmitting?: boolean;
    initialValue?: RichTextValue | null;
    submitTitle?: ReactNode;
    className?: string;
    style?: React.CSSProperties;
} & Omit<RichTextInputProps, 'value' | 'onChange' | 'onAIInteraction'>;

export const RichTextForm = ({
    allowEmpty = false,
    submitTitle,
    onSubmit,
    onCancel,
    placeholder,
    isSubmitting = false,
    initialValue = null,
    className,
    style,
    ...props
}: RichTextForm) => {
    const { t } = useLocale('common');
    const { currentSessionId } = useVoiceRecording();
    const [content, setContent] = useState<RichTextValue | null>(initialValue);

    const [isAIBusy, setIsAIBusy] = useState<boolean>(false);
    const [aiSessionId, setAISessionId] = useState<string | undefined>(undefined);

    const handleAIInteraction = (action: AIActionType, sessionId: string) => {
        switch (action) {
            case 'accept':
                // Store the session ID for any AI interaction (we'll use it when the note is created)
                setAISessionId(sessionId);
                setIsAIBusy(false);
                break;
            case 'request':
            case 'regenerate':
                setIsAIBusy(true);
                break;
            case 'reject':
                setIsAIBusy(false);
                break;
            default:
                // no-op for unknown actions
                break;
        }
    };

    const handleSubmit = () => {
        if (content && !allowEmpty) {
            onSubmit(content, aiSessionId);
            setContent(null);
        }
    };

    const handleCancel = () => {
        setContent(null);
        onCancel();
    };

    // Check if content has any meaningful text (not just whitespace)
    const hasValidContent =
        content &&
        content.root?.children?.some((child: any) => {
            return child.children?.some((textNode: any) => textNode.text?.trim());
        });

    const canSubmit = (hasValidContent || allowEmpty) && !isSubmitting && !currentSessionId && !isAIBusy;

    return (
        <div className={cn('flex flex-col gap-3', className)} style={style}>
            <div className="rounded-lg bg-white">
                <RichTextInput
                    placeholder={placeholder}
                    value={content}
                    onChange={setContent}
                    onAIInteraction={handleAIInteraction}
                    {...props}
                />
            </div>
            <div className="flex justify-end items-center">
                <div className="flex gap-2">
                    <Button variant="subtle" size="sm" onClick={handleCancel} disabled={isSubmitting}>
                        {t('form__cancel')}
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        isLoading={isSubmitting}
                    >
                        {submitTitle ?? t('form__submit')}
                    </Button>
                </div>
            </div>
        </div>
    );
};
